import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { ForecastError } from '@/features/forecasts/api/get-kpi-forecasts';
import { ForecastChart } from '@/features/forecasts/components/forecast-chart';
import { ForecastEmptyStateAlert } from '@/features/forecasts/components/forecast-empty-state-alert';
import {
  ForecastTimeCard,
  ForecastTimeCardSkeleton,
} from '@/features/forecasts/components/forecast-time-card';
import { ForecastsHubFilter } from '@/features/forecasts/components/forecasts-hub-filter';
import { useForecastsFilterStore } from '@/features/forecasts/hooks/use-forecasts-hub-filter';
import { useForecastsHubStore } from '@/features/forecasts/hooks/use-forecasts-hub-store';
import { useForecastsProjectsFiltered } from '@/features/forecasts/hooks/use-forecasts-projects-filtered';
import { useKpiForecasts } from '@/features/forecasts/hooks/use-kpi-forecasts';
import { BlurredContainer } from '@/features/insights/components/blurred-container';
import { KpiSelectionRow } from '@/features/insights/components/kpi-selection-row';
import { useProjectKpisWithMetrics } from '@/features/projects/project-details/hooks/use-project-kpis';
import {
  getKpiUniqueId,
  isDerivativeKpi,
} from '@/features/projects/shared/utils/kpi-utils';
import { DefaultLayout } from '@/shared/components/layouts/default-layout';
import { CenteredLoadingIndicator } from '@/shared/components/loading-indicator';
import { getTendency } from '@/shared/components/tendency-indicator';
import { KpiWithMetrics } from '@/shared/types/api';
import { KPI_UNITS_CODES, KpiUnit } from '@/shared/types/kpi';

export const ForecastsHubPage = () => {
  const {
    data: projects,
    isLoading: isProjectsLoading,
    isError: isProjectsError,
  } = useForecastsProjectsFiltered();
  const { projectIds, subprojectIds, setProjectIds } =
    useForecastsFilterStore();
  const { uniqueKpiId, setUniqueKpiId } = useForecastsHubStore();

  const projectId = subprojectIds?.length
    ? Number(subprojectIds[0])
    : projectIds?.length
      ? Number(projectIds[0])
      : projects?.[0]?.id;
  const matchingProject = projects?.find((p) => p.id === projectId);

  const [projectKpisQuery, derivativeKpisQuery] = useProjectKpisWithMetrics({
    projectId: matchingProject?.id,
    projectType: matchingProject?.type,
    enabled: !!matchingProject,
  });
  const kpis = [
    ...(projectKpisQuery.data?.filter(Boolean) || []),
    ...(derivativeKpisQuery.data?.filter(Boolean) || []),
  ] as KpiWithMetrics[];
  const kpisLoading =
    projectKpisQuery.isLoading || derivativeKpisQuery.isLoading;
  const currentKpi =
    kpis?.find((k) => getKpiUniqueId(k) === uniqueKpiId) || kpis?.[0];

  const kpiVariation = currentKpi
    ? isDerivativeKpi(currentKpi)
      ? 'derivative'
      : 'standard'
    : 'standard';
  const {
    data: forecastsData,
    isLoading: isForecastsLoading,
    error: forecastsError,
  } = useKpiForecasts(currentKpi?.id, projectId, kpiVariation);
  const isForecastsError = forecastsError !== null;

  const { t } = useTranslation('forecast', { keyPrefix: 'ForecastsHub' });

  const hasNoData =
    (projects && projects.length === 0) || !kpis || kpis.length === 0;

  const getForecastErrorMessage = () => {
    if (!forecastsError) return null;

    if (forecastsError instanceof ForecastError) {
      return t(`errors.${forecastsError.code}` as const);
    }

    return t('errors.unknown_error');
  };

  // Auto-select first project if none selected
  useEffect(() => {
    if (!projectIds?.length && projects && projects.length > 0) {
      setProjectIds([projects[0].id.toString()]);
    }
  }, [projectIds, projects, setProjectIds]);

  return (
    <DefaultLayout
      title={t('title')}
      titleBig
      headerActions={isProjectsLoading ? [] : [<ForecastsHubFilter />]}
    >
      {isProjectsLoading ? (
        <CenteredLoadingIndicator />
      ) : !projects || isProjectsError ? (
        <span>{t('errors.fetch_projects')}</span>
      ) : (
        <div className="mb-6 flex flex-col gap-6">
          <BlurredContainer isBlurred={hasNoData}>
            <KpiSelectionRow
              kpis={matchingProject ? kpis : undefined}
              currentKpi={currentKpi}
              setKpiId={setUniqueKpiId}
            />
          </BlurredContainer>

          <div className="flex flex-col gap-6">
            <div className="flex w-full flex-row gap-6">
              {isForecastsLoading || hasNoData || isForecastsError ? (
                <>
                  <ForecastTimeCardSkeleton />
                  <ForecastTimeCardSkeleton />
                  <ForecastTimeCardSkeleton />
                </>
              ) : currentKpi && forecastsData?.forecasts ? (
                <>
                  {forecastsData.forecasts
                    .sort((a, b) => {
                      return a.period_type.localeCompare(b.period_type);
                    })
                    .map((periodGroup) => {
                      const firstForecast = periodGroup.forecasts[0];
                      if (!firstForecast) return null;

                      return (
                        <ForecastTimeCard
                          key={periodGroup.period_type}
                          timePeriod={periodGroup.period_type}
                          value={Math.round(firstForecast.forecast_value)}
                          tendency={getTendency(
                            firstForecast.forecast_value,
                            firstForecast.previous_period_value,
                          )}
                          kpiUnit={
                            currentKpi.unit ??
                            KpiUnit.fromString(KPI_UNITS_CODES.EUR)
                          }
                        />
                      );
                    })}
                </>
              ) : null}
            </div>
            <div className="relative">
              <BlurredContainer
                isBlurred={isForecastsLoading || hasNoData || isForecastsError}
              >
                <ForecastChart
                  key={currentKpi?.id}
                  kpi={currentKpi}
                  projectId={projectId}
                  matchingProject={matchingProject}
                  justCard={hasNoData || isForecastsError}
                />
              </BlurredContainer>
              {(!forecastsData || hasNoData || isForecastsError) &&
                !(isForecastsLoading || kpisLoading) && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <ForecastEmptyStateAlert
                      title={t('errors.no_forecasts_available')}
                      description={
                        getForecastErrorMessage() ??
                        t('errors.no_forecasts_available_description')
                      }
                    />
                  </div>
                )}
            </div>
          </div>
        </div>
      )}
    </DefaultLayout>
  );
};
